/**
 * Audit-event canonicalisation, hash chaining, and verification.
 *
 * Pure and framework-free on purpose: the writer, the verifier, and any future re-verification job
 * must compute byte-identical hashes, and the only way to guarantee that is for all of them to call
 * the same function. Two "equivalent" hash implementations is a chain that fails verification for a
 * reason nobody can find.
 *
 * See `docs/roadmap.md` §3.2. HIPAA §164.312(b) requires audit controls over PHI access — reads as
 * well as writes — and the trail is only evidence if it is tamper-evident, so each event commits to
 * its predecessor.
 *
 * WHAT THIS DOES AND DOES NOT PROVE
 *
 * A hash chain detects *alteration* and *removal* of events by anyone who cannot recompute the whole
 * chain. It does not prevent an attacker with write access to the chain-state row and every
 * subsequent event from rewriting history wholesale, and it does not prove an event was ever written
 * in the first place. Those need an append-only external store (S3 Object Lock) and periodic
 * verification against it — both are separate backlog items (U5, U6). This module is one layer of
 * that, not the whole story, and claiming otherwise in a compliance conversation would be wrong.
 */

import {
  ACCESS_BASES as CHART_ACCESS_BASES,
  type AccessBasis as ChartAccessBasis,
} from './careTeam.ts';

/** Actions worth distinguishing in an audit trail. `read` is the one HIPAA specifically requires. */
export const AUDIT_ACTIONS = [
  'read',
  'create',
  'update',
  'delete',
  'export',
  'login',
  'logout',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * Why the actor was permitted to touch this record.
 *
 * Derived from `careTeam.ts`'s `AccessBasis` rather than restated, which is the whole point: a
 * break-glass access that audits as ordinary treatment access defeats having break-glass at all, and
 * two independent lists of basis values is exactly how that drift happens. A first draft of this
 * module did declare its own copy, and the typecheck caught the collision — the compiler enforcing
 * the rule the comment claims.
 *
 * The audit vocabulary is a strict superset: it adds `system` for actions with no human actor and
 * therefore no care-team relationship (a nightly claims batch, a scheduled export). A chart-access
 * decision can never produce `system`, so extending here rather than widening `careTeam.ts` keeps that
 * asymmetry honest.
 *
 * `patientSelfService` (`BACKLOG.md` C7, `emr-po` A16 decision 5) is the second addition, landed
 * 2026-08-24 alongside the kiosk check-in surface: a write made by the *patient themselves*, through
 * a token-scoped session (`server/src/auth/patientScopedTokens.ts`) rather than a staff member's own
 * care-team or administrative relationship to the record. Named generically — "patient self-service"
 * — rather than "kiosk", since `BACKLOG.md` C19/C21 are named follow-ups that reuse the identical
 * token mechanism for a driver's-license scan and an SMS survey link respectively, and both need this
 * same access-basis category rather than a kiosk-specific one. Deliberately its own value rather than
 * folded into `administrative` or `treatment`: neither describes a patient acting on their own
 * record, and A16's decision 5 is explicit that this needs to be distinguishable in the audit trail
 * from ordinary staff access, not just an easy-to-produce label reusing the closest existing one.
 */
/**
 * `disclosure` (`BACKLOG.md` C14, `docs/decisions/cmia-disclosure-scope.md`), added alongside
 * `system`/`patientSelfService`: used exclusively by `patient_disclosures` writes (and any future
 * bulk de-identified-export writes) — "why did content leave the practice's boundary to a third
 * party," never returned by `evaluateChartAccess`/`decideChartAccess` as a chart-access grant. Kept
 * distinct from `administrative` on purpose: collapsing a third-party release into the closest
 * existing basis would blur exactly the distinction CMIA's §56.36 private-right-of-action exposure
 * makes matter — whether a given data movement was a disclosure event subject to CMIA's own rules at
 * all.
 */
export const AUDIT_ONLY_ACCESS_BASES = ['system', 'patientSelfService', 'disclosure'] as const;
export const AUDIT_ACCESS_BASES = [...CHART_ACCESS_BASES, ...AUDIT_ONLY_ACCESS_BASES] as const;
export type AuditAccessBasis = ChartAccessBasis | (typeof AUDIT_ONLY_ACCESS_BASES)[number];

/**
 * One audit event, in the shape that gets hashed.
 *
 * Every field is part of the hash. Adding a field is therefore a chain-format change: events written
 * before and after will hash differently for the same logical content, so verification must be
 * version-aware. `formatVersion` exists so that is an explicit, detectable transition rather than a
 * silent chain break — bump it when the field set changes, and teach the verifier both shapes.
 */
export interface AuditEvent {
  readonly formatVersion: 1;

  /** Time the action happened, ISO 8601 with offset. Server-generated; never client-supplied. */
  readonly occurredAt: string;

  /** Tenant. Also the chain shard key, with the date. */
  readonly orgId: string;

  /** The authenticated principal. */
  readonly actorUserId: string;

  /**
   * When an operator is acting as another user, the operator's own identity. Dual identity matters:
   * "who really did this" and "whose account was it done under" are different questions and an
   * incident review needs both.
   */
  readonly onBehalfOfUserId: string | null;

  readonly action: AuditAction;

  /** Logical table or resource type, e.g. `clinical_note`. */
  readonly resourceType: string;
  readonly resourceId: string | null;

  /** The patient whose PHI this concerns, when applicable. What "show me every access" queries on. */
  readonly patientId: string | null;

  /**
   * Field names touched. Names only — never values. An audit trail that records the values it
   * observed becomes a second, less-guarded copy of the PHI it exists to protect, and the workspace
   * invariant is to audit that an action happened, never what was in it.
   */
  readonly fields: readonly string[];

  /** The record's confidentiality label at the time of access, so sensitive access is queryable. */
  readonly confidentialityReasons: readonly string[];

  readonly accessBasis: AuditAccessBasis;

  /** Required and non-empty when `accessBasis` is `breakGlass`; null otherwise. */
  readonly justification: string | null;

  /** Server-resolved. Never read from a client-supplied header without a trusted proxy in front. */
  readonly sourceIp: string | null;
  readonly userAgent: string | null;

  /** Outcome, so denied attempts are auditable too — often the more interesting signal. */
  readonly outcome: 'allowed' | 'denied';
}

/**
 * Deterministic serialisation for hashing.
 *
 * Hand-rolled rather than `JSON.stringify(event)` because stringify preserves *insertion* order, so
 * two objects with identical content but different construction order serialise differently and hash
 * differently. That would produce verification failures that look like tampering and are not — the
 * worst possible false positive for this system, since it would train people to ignore chain
 * warnings.
 *
 * Field order here is fixed and must never be reordered without bumping `formatVersion`.
 */
export function canonicalize(event: AuditEvent): string {
  const ordered: readonly (readonly [string, unknown])[] = [
    ['formatVersion', event.formatVersion],
    ['occurredAt', event.occurredAt],
    ['orgId', event.orgId],
    ['actorUserId', event.actorUserId],
    ['onBehalfOfUserId', event.onBehalfOfUserId],
    ['action', event.action],
    ['resourceType', event.resourceType],
    ['resourceId', event.resourceId],
    ['patientId', event.patientId],
    // Sorted, so the same set of fields hashes identically regardless of the order a handler
    // happened to collect them in.
    ['fields', [...event.fields].sort()],
    ['confidentialityReasons', [...event.confidentialityReasons].sort()],
    ['accessBasis', event.accessBasis],
    ['justification', event.justification],
    ['sourceIp', event.sourceIp],
    ['userAgent', event.userAgent],
    ['outcome', event.outcome],
  ];
  return JSON.stringify(ordered);
}

/** Shard key. Per org and per UTC day — see `chainShardKey` for why. */
export interface ChainShard {
  readonly orgId: string;
  /** `YYYY-MM-DD`, UTC. */
  readonly day: string;
}

/**
 * The chain shard an event belongs to.
 *
 * Sharding avoids a specific scaling defect: a single global chain tip advanced under
 * `SELECT ... FOR UPDATE` on one row serialises *every* audited write in the entire application
 * behind one lock. At thousands of practices that is not a tuning concern, it is a hard ceiling —
 * so the shard is per org and per day from the start.
 *
 * Per day as well as per org bounds how much of a chain a verification job must read to check recent
 * history, and gives a natural partition boundary for the S3 layout.
 *
 * Deriving the day from `occurredAt` rather than from the current clock keeps an event's shard stable
 * on replay, which is what makes verification reproducible.
 */
export function chainShardKey(event: AuditEvent): ChainShard {
  const day = event.occurredAt.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    throw new Error(`audit event occurredAt is not an ISO 8601 timestamp: ${event.occurredAt}`);
  }
  return { orgId: event.orgId, day };
}

export function formatShardKey(shard: ChainShard): string {
  return `${shard.orgId}/${shard.day}`;
}

/** The value hashed for the first event in a shard. */
export const GENESIS_HASH = '0'.repeat(64);

/**
 * Digest function, injected rather than imported.
 *
 * `shared` is imported by both the server and (later) the browser, which have different crypto APIs.
 * Taking the digest as a parameter keeps this module genuinely environment-free instead of forcing a
 * Node import into a package the web app also loads.
 */
export type Sha256Hex = (input: string) => string;

/** Hash for one event, committing to its predecessor. */
export function computeEntryHash(
  event: AuditEvent,
  previousHash: string,
  sha256Hex: Sha256Hex,
): string {
  if (!/^[0-9a-f]{64}$/.test(previousHash)) {
    // A malformed predecessor means the chain state is already corrupt. Refusing here stops a bad
    // hash being cemented into every subsequent event.
    throw new Error(`previousHash is not a sha256 hex digest: ${previousHash}`);
  }
  return sha256Hex(`${previousHash}\n${canonicalize(event)}`);
}

export interface ChainEntry {
  readonly event: AuditEvent;
  readonly previousHash: string;
  readonly entryHash: string;
}

export type ChainVerdict =
  | { readonly ok: true; readonly entriesChecked: number }
  | {
      readonly ok: false;
      readonly entriesChecked: number;
      /** Index of the first entry that failed. */
      readonly failedAtIndex: number;
      readonly reason: 'hash-mismatch' | 'broken-link' | 'wrong-genesis' | 'shard-mismatch';
      readonly detail: string;
    };

/**
 * Re-derives every hash in a shard segment and checks the links.
 *
 * Reports the *first* failure rather than collecting all of them, deliberately: once one entry is
 * altered, every later hash mismatches as a consequence, so a list of failures would be one real
 * finding plus noise. The first index is the actionable fact.
 */
export function verifyChainSegment(
  entries: readonly ChainEntry[],
  sha256Hex: Sha256Hex,
  options: { readonly expectGenesis?: boolean } = {},
): ChainVerdict {
  const expectGenesis = options.expectGenesis ?? true;

  let expectedPrevious = expectGenesis ? GENESIS_HASH : entries[0]?.previousHash;
  let shard: string | undefined;

  for (let index = 0; index < entries.length; index += 1) {
    const entry = entries[index];
    if (entry === undefined) continue;

    const entryShard = formatShardKey(chainShardKey(entry.event));
    shard ??= entryShard;
    if (entryShard !== shard) {
      return {
        ok: false,
        entriesChecked: index,
        failedAtIndex: index,
        reason: 'shard-mismatch',
        detail: `entry belongs to shard ${entryShard}, segment is ${shard}`,
      };
    }

    if (index === 0 && expectGenesis && entry.previousHash !== GENESIS_HASH) {
      return {
        ok: false,
        entriesChecked: 0,
        failedAtIndex: 0,
        reason: 'wrong-genesis',
        detail: `first entry in a shard must chain from the genesis hash, found ${entry.previousHash}`,
      };
    }

    if (expectedPrevious !== undefined && entry.previousHash !== expectedPrevious) {
      return {
        ok: false,
        entriesChecked: index,
        failedAtIndex: index,
        reason: 'broken-link',
        detail: `expected previousHash ${expectedPrevious}, found ${entry.previousHash} — an entry was removed, reordered, or inserted`,
      };
    }

    const recomputed = computeEntryHash(entry.event, entry.previousHash, sha256Hex);
    if (recomputed !== entry.entryHash) {
      return {
        ok: false,
        entriesChecked: index,
        failedAtIndex: index,
        reason: 'hash-mismatch',
        detail: `stored hash ${entry.entryHash} does not match recomputed ${recomputed} — the event content was altered`,
      };
    }

    expectedPrevious = entry.entryHash;
  }

  return { ok: true, entriesChecked: entries.length };
}

/**
 * Does this event require a justification, and does it have one?
 *
 * Kept here rather than only at the call site so the rule is enforced at the moment of hashing — an
 * unjustified break-glass event that reaches the chain is already a compliance gap, and rejecting it
 * at write time is the only place that helps.
 */
export function auditEventProblem(event: AuditEvent): string | null {
  if (event.accessBasis === 'breakGlass') {
    if (event.justification === null || event.justification.trim() === '') {
      return 'a breakGlass audit event requires a non-empty justification';
    }
  } else if (event.justification !== null) {
    return `justification must be null unless accessBasis is breakGlass (got basis ${event.accessBasis})`;
  }
  if (event.action === 'read' && event.outcome === 'allowed' && event.patientId === null) {
    // Not every read concerns a patient, but a PHI read that records no patient cannot answer
    // "who accessed this patient's record", which is the question the trail exists for. Callers
    // reading non-patient resources should pass a resourceType that says so.
    return null;
  }
  return null;
}
